import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Grid } from "@mui/material";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { unitsApiKeyStore } from "../../api/units-api-key-store";
import { officeApiKeyStore } from "../../api/office-api-key-store";
import { projectsApiKeyStore } from "../../api/projects-api-key-store";
import { customersApiKeyStore } from "../../api/customers-api-key-store";
import { planReviewApiKeyStore } from "../../api/plan-review-api-key-store";
import FormInput from "../query-builder/hooks-form-querybuilder/form-input.component";
import { FormAutoCompleteSelectDynamic } from "../query-builder/hooks-form-querybuilder/form-autocomplete-select.dynamic.component";
import { FormCheckbox } from "../../components/hook-form/form-checkbox.component";

interface FiltersComponent {
    checkboxDisabled?: boolean;
    moduleCode: string;
}

export const FiltersComponent = ({ checkboxDisabled, moduleCode }: FiltersComponent) => {

    const { setValue } = useFormContext();
    const [officeDisabled, setOfficeDisabled] = useState<boolean>(true);
    const [projectDisabled, setProjectDisabled] = useState<boolean>(true);
    const [unitDisabled, setUnitDisabled] = useState<boolean>(true);
    const [assignedToDisabled, setAssignedToDisabled] = useState<boolean>(true);

    const handleCustomerChange = (value: any) => {
        setOfficeDisabled(!value);
        setProjectDisabled(true);
        setUnitDisabled(true);
        setValue('office', { officeID: '', officeName: '' });
        setValue('project', { projectID: '', projectName: '' });
        setValue('jobsite', { unitID: '', jobsite: '' });
        setValue('unit', { unitID: '', unitName: '' });
    };

    const handleOfficeChange = (value: any) => {
        setProjectDisabled(!value);
        setAssignedToDisabled(!value);
        setUnitDisabled(true);
        setValue('project', { projectID: '', projectName: '' });
        setValue('jobsite', { unitID: '', jobsite: '' });
        setValue('unit', { unitID: '', unitName: '' });
        setValue('assignedTo', { unitID: '', fullName: '' });
    };

    const handleProjectChange = (value: any) => {
        setUnitDisabled(!value);
        setValue('jobsite', { unitID: '', jobsite: '' });
        setValue('unit', { unitID: '', unitName: '' });
    };

    const handleJobsiteChange = (value: any) => {
        if (value) setValue('unit', { unitID: value?.unitID, unitName: value?.unitName });
        else setValue('unit', { unitID: '', unitName: '' });
    };

    return (
        <Grid container spacing={1}>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
                <FormAutoCompleteSelectDynamic
                    name="customer"
                    label="Customer"
                    onChange={handleCustomerChange}
                    getQueryKeyConfig={() => customersApiKeyStore.getCustomers()}
                    optionsValueKey="customerID"
                    optionsLabelKey="customerName"
                />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
                <FormAutoCompleteSelectDynamic
                    name="office"
                    label="Offices"
                    disabled={officeDisabled}
                    watchKeys={['customer']}
                    onChange={handleOfficeChange}
                    getQueryKeyConfig={(inputData: any) =>
                        officeApiKeyStore.getCustomerOffices(inputData.customer?.customerID)
                    }
                    optionsValueKey="officeID"
                    optionsLabelKey="officeName"
                />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
                <FormAutoCompleteSelectDynamic
                    name="project"
                    label="Projects"
                    disabled={projectDisabled}
                    watchKeys={['office']}
                    onChange={handleProjectChange}
                    getQueryKeyConfig={(inputData: any) => projectsApiKeyStore.getOfficeProjects(inputData.office?.officeID)}
                    optionsValueKey="projectID"
                    optionsLabelKey="projectName"
                />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
                <FormAutoCompleteSelectDynamic
                    name="jobsite"
                    label="Jobsite"
                    onChange={handleJobsiteChange}
                    disabled={unitDisabled}
                    watchKeys={['project']}
                    getQueryKeyConfig={(inputData: any) => unitsApiKeyStore.getProjectUnits(inputData.project?.projectID)}
                    optionsValueKey="unitID"
                    optionsLabelKey="jobsite"
                />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
                <FormAutoCompleteSelectDynamic
                    name="unit"
                    label="Lot/Unit"
                    disabled={unitDisabled}
                    watchKeys={['project']}
                    getQueryKeyConfig={(inputData: any) => unitsApiKeyStore.getProjectUnits(inputData.project?.projectID)}
                    optionsValueKey="unitID"
                    optionsLabelKey="unitName"
                />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
                <FormAutoCompleteSelectDynamic
                    name="assignedTo"
                    label="Assigned To"
                    disabled={assignedToDisabled}
                    watchKeys={['office']}
                    getQueryKeyConfig={(inputData: any) => planReviewApiKeyStore.getPlanTaskAssigned(
                        { officeID: inputData.office?.officeID, moduleCode: moduleCode })}
                    optionsValueKey="userID"
                    optionsLabelKey="fullName"
                />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
                <FormAutoCompleteSelectDynamic
                    name="status"
                    label="Status"
                    getQueryKeyConfig={() => planReviewApiKeyStore.getPlanReviewFilterStatus(moduleCode)}
                    optionsValueKey="planReviewTaskStatusID"
                    optionsLabelKey="planReviewTaskStatusName"
                />
            </Grid>
            <Grid item xl={checkboxDisabled ? 2.3 : 3} lg={checkboxDisabled ? 2.3 : 3} sm={4} xs={6}>
                <FormInput
                    name="address"
                    label="Address"
                />
            </Grid>
            <Grid item xl={checkboxDisabled ? 0.7 : 6} lg={checkboxDisabled ? 0.7 : 6} sm={4} xs={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!checkboxDisabled && (<FormCheckbox label="Include Completed Items" name="includeCompleted" />)}
                <Button type="submit" title="Filter" variant="contained" startIcon={<FilterAltOutlinedIcon />}>
                    Filter
                </Button>
            </Grid>
        </Grid>
    );
}